import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useSuppliers, useDeleteSupplier } from '@/hooks/use-suppliers';
import { useOpenCreateFromQuery } from '@/hooks/use-open-create-from-query';
import { usePurchaseDocuments } from '@/hooks/use-purchases';
import { usePayments } from '@/hooks/use-payments';
import { computeSupplierBalance } from '@erp/domain/payments';
import type { Supplier, SupplierFilters, PurchaseDocument, Payment } from '@erp/data-access/interfaces';
import { SupplierFormDialog } from './SupplierFormDialog';
import {
  Badge, Button, Card, CardContent, CardHeader,
  DataTable, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  EmptyState, FilterBar, FilterField, Input, PageHeader,
  cn,
  type ColumnDef,
} from '@erp/ui';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Truck } from '@/components/icons';
import { useColumnConfig, ColumnConfigDialog, ColumnConfigButton, type ColDef } from '@/components/ui/column-config';
import { SearchableSelect } from '@/components/ui/searchable-select';

const ALL_COLUMNS: ColDef[] = [
  { key: 'code', labelKey: 'suppliers.code', locked: true },
  { key: 'name', labelKey: 'suppliers.name' },
  { key: 'type', labelKey: 'suppliers.type' },
  { key: 'taxId', labelKey: 'suppliers.taxId' },
  { key: 'city', labelKey: 'suppliers.city' },
  { key: 'phone', labelKey: 'suppliers.phone' },
  { key: 'email', labelKey: 'suppliers.email' },
  { key: 'notes', labelKey: 'suppliers.notes' },
  { key: 'balance', labelKey: 'payments.balance' },
];

const DEFAULT_COLUMNS = ['code', 'name', 'type', 'taxId', 'city', 'phone', 'balance'];

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function SuppliersPage() {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState<Supplier | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Supplier | null>(null);
  const [colConfigOpen, setColConfigOpen] = useState(false);

  const { visibleCols, toggleCol, moveCol, resetCols, allColumns } = useColumnConfig({
    storageKey: 'erp_supplier_columns',
    allColumns: ALL_COLUMNS,
    defaultColumns: DEFAULT_COLUMNS,
  });

  const filters: SupplierFilters = useMemo(() => ({
    search: search || undefined,
    type: (typeFilter as 'company' | 'individual') || undefined,
  }), [search, typeFilter]);

  const { data: suppliers, isLoading } = useSuppliers(filters);
  const { data: allPurchaseDocs } = usePurchaseDocuments();
  const { data: allPayments } = usePayments({ thirdPartyType: 'supplier' });
  const deleteMutation = useDeleteSupplier();

  const balances = useMemo(() => {
    const map = new Map<string, number>();
    if (!suppliers) return map;
    for (const s of suppliers) {
      const supplierPayments = (allPayments ?? []).filter((p: Payment) => p.thirdPartyId === s.id);
      const supplierDocs = (allPurchaseDocs ?? [])
        .filter((d: PurchaseDocument) => d.supplierId === s.id && (d.docType === 'invoice' || d.docType === 'credit_note'))
        .map((d: PurchaseDocument) => ({
          docType: d.docType as 'invoice' | 'credit_note',
          netToPay: Number(d.netToPay) > 0 ? Number(d.netToPay) : Number(d.totalTtc),
        }));
      map.set(s.id, computeSupplierBalance(
        supplierPayments.map((p) => ({ amount: p.amount, paymentType: p.paymentType })),
        supplierDocs,
      ));
    }
    return map;
  }, [suppliers, allPurchaseDocs, allPayments]);

  const handleEdit = (s: Supplier) => { setEditSupplier(s); setFormOpen(true); };
  const handleNew = () => { setEditSupplier(null); setFormOpen(true); };
  useOpenCreateFromQuery(handleNew);
  const handleDelete = async () => {
    if (toDelete) { await deleteMutation.mutateAsync(toDelete.id); setDeleteOpen(false); }
  };

  const allColumnDefs: Record<string, ColumnDef<Supplier, unknown>> = {
    code: { accessorKey: 'code', header: t('suppliers.code'), cell: ({ row }) => <span className="font-mono text-xs uppercase tracking-wide text-muted-foreground">{row.original.code}</span> },
    name: { accessorKey: 'name', header: t('suppliers.name'), cell: ({ row }) => <span className="font-medium">{row.original.name}</span> },
    type: {
      accessorKey: 'type',
      header: t('suppliers.type'),
      enableSorting: false,
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] font-medium',
            row.original.type === 'company'
              ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400'
              : 'border-border bg-muted/50 text-muted-foreground',
          )}
        >
          {row.original.type === 'company' ? t('suppliers.typeCompany') : t('suppliers.typeIndividual')}
        </Badge>
      ),
    },
    taxId: { accessorKey: 'taxId', header: t('suppliers.taxId'), cell: ({ row }) => row.original.taxId ?? <span className="text-muted-foreground">—</span> },
    city: { accessorKey: 'city', header: t('suppliers.city'), cell: ({ row }) => row.original.city ?? <span className="text-muted-foreground">—</span> },
    phone: { accessorKey: 'phone', header: t('suppliers.phone'), cell: ({ row }) => row.original.phone ?? <span className="text-muted-foreground">—</span> },
    email: { accessorKey: 'email', header: t('suppliers.email'), cell: ({ row }) => row.original.email ?? <span className="text-muted-foreground">—</span> },
    notes: {
      id: 'notes',
      header: t('suppliers.notes'),
      enableSorting: false,
      cell: ({ row }) => <span className="block max-w-[220px] truncate text-muted-foreground">{row.original.notes ?? '—'}</span>,
    },
    balance: {
      id: 'balance',
      header: () => <span className="block text-end">{t('payments.balance')}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const b = balances.get(row.original.id) ?? 0;
        if (Math.abs(b) < 0.001) return <span className="block text-end text-muted-foreground tabular-nums">0,000 DT</span>;
        const isCredit = b > 0;
        return (
          <span
            className={cn(
              'block text-end tabular-nums font-medium',
              isCredit ? 'text-green-600 dark:text-green-400' : 'text-destructive',
            )}
            title={isCredit ? t('payments.creditBalance') : t('payments.debtBalance')}
          >
            {isCredit ? '+' : ''}{formatPrice(b)} DT
          </span>
        );
      },
    },
  };

  const columns: ColumnDef<Supplier, unknown>[] = [
    ...visibleCols.map((k) => allColumnDefs[k]).filter(Boolean) as ColumnDef<Supplier, unknown>[],
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-xs"><MoreHorizontal size={14} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                <Pencil size={14} />{t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={() => { setToDelete(row.original); setDeleteOpen(true); }}>
                <Trash2 size={14} />{t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const hasActiveFilters = Boolean(search || typeFilter);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('suppliers.title')}
        actions={
          <Button onClick={handleNew}>
            <Plus data-icon="inline-start" size={16} />
            {t('suppliers.newSupplier')}
          </Button>
        }
      />

      <Card className="transition-all duration-200">
        <CardHeader className="pb-3">
          <FilterBar
            onClear={hasActiveFilters ? () => { setSearch(''); setTypeFilter(''); } : undefined}
            clearLabel={t('common.clearFilters')}
            rightSlot={<ColumnConfigButton onClick={() => setColConfigOpen(true)} />}
          >
            <FilterField label={t('common.search')} grow>
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5" />
                <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-8 h-8" />
              </div>
            </FilterField>
            <FilterField label={t('suppliers.type')} minWidth={200}>
              <SearchableSelect
                options={[{ value: 'company', label: t('suppliers.typeCompany') }, { value: 'individual', label: t('suppliers.typeIndividual') }]}
                value={typeFilter}
                onChange={setTypeFilter}
                label={t('suppliers.type')}
                placeholder={t('common.all')}
                searchPlaceholder={t('common.search')}
                className="h-8"
              />
            </FilterField>
          </FilterBar>
        </CardHeader>
        <CardContent className="pt-0">
          <DataTable
            data={suppliers ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`/suppliers/${r.id}`)}
            isLoading={isLoading}
            emptyState={
              <EmptyState
                icon={<Truck size={18} />}
                title={t('common.noResults')}
                action={<Button variant="outline" size="sm" onClick={handleNew}>
                    <Plus data-icon="inline-start" size={14} />
                    {t('suppliers.newSupplier')}
                  </Button>
                }
              />
            }
            pagination
            pageSize={10}
            rowsPerPageLabel={t('dashboard.rowsPerPage')}
            ofLabel={t('dashboard.of')}
            minWidth={720}
          />
        </CardContent>
      </Card>

      <SupplierFormDialog open={formOpen} onOpenChange={setFormOpen} supplier={editSupplier} />

      <ColumnConfigDialog
        open={colConfigOpen}
        onOpenChange={setColConfigOpen}
        visibleCols={visibleCols}
        allColumns={allColumns}
        toggleCol={toggleCol}
        moveCol={moveCol}
        resetCols={resetCols}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('suppliers.deleteConfirm')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('suppliers.deleteWarning')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
